import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, PartyPopper, Share2 } from "lucide-react";

const KEY = "wua.journey.completeCelebrated";
const COLOURS = ["#7C3AED", "#0891B2", "#002147", "#4C1D95", "#92400E", "#B45309", "#10B981"];
const BITS = Array.from({ length: 28 }, (_, i) => ({ x: (i * 37) % 100, d: (i % 7) * 0.08, c: COLOURS[i % COLOURS.length], r: (i * 53) % 360 }));

// First sighting in this browser gets the confetti; afterwards the panel stays, quietly.
function useFirstTime(loaded, active) {
  const [first, setFirst] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    // A reset stage re-arms the confetti for the next finish.
    if (!active) { try { localStorage.removeItem(KEY); } catch { /* ignore */ } return; }
    try {
      if (localStorage.getItem(KEY)) return;
      localStorage.setItem(KEY, "1");
    } catch { /* private mode */ }
    setFirst(true);
  }, [loaded, active]);
  return first;
}

// Shown once the five planning stages are done; `applied` (every application exported) finishes the last stop.
export function JourneyComplete({ active, loaded, applied }) {
  const first = useFirstTime(loaded, active);
  const reduce = useReducedMotion();
  if (!active) return null;
  return (
    <motion.section data-testid="journey-complete" data-first={first ? "true" : "false"}
      initial={reduce ? false : { opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}
      className="relative mb-8 overflow-hidden rounded-2xl bg-[#1C1433] p-6 text-white sm:p-10">
      {first && !reduce && BITS.map((b, i) => (
        <motion.span key={i} aria-hidden className="pointer-events-none absolute top-0 h-2.5 w-1.5 rounded-sm"
          style={{ left: `${b.x}%`, backgroundColor: b.c }}
          initial={{ y: -20, opacity: 1, rotate: 0 }} animate={{ y: 320, opacity: 0, rotate: b.r }}
          transition={{ duration: 2.2, delay: b.d, ease: "easeIn" }} />
      ))}
      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-emerald-300">
            <PartyPopper className="h-4 w-4" aria-hidden /> Journey complete
          </p>
          <h2 className="mt-3 font-editorial text-3xl leading-tight sm:text-4xl" data-testid="journey-complete-heading">
            {applied ? "All six stages done. Every application is exported." : "Five stages done. You're ready to apply."}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-white/75 sm:text-base" data-testid="journey-complete-body">
            {applied
              ? "Your files are ready for UCAS, Common App and each university. Share the plan with your family, then send them off before each deadline."
              : "Your subject, city, chances, assessment and final list are all in place. Share the plan with your family, then turn each course on your shortlist into an application."}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
          <Link to="/shortlist/parent-report" data-testid="journey-complete-parent-report"
            className="group inline-flex min-h-[48px] items-center justify-between gap-3 rounded-lg bg-white px-5 text-sm font-semibold text-[#1C1433] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300">
            <span className="inline-flex items-center gap-2"><Share2 className="h-4 w-4" aria-hidden /> Share the parent report</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
          </Link>
          <Link to="/applications" data-testid="journey-complete-start-applying"
            className="group inline-flex min-h-[48px] items-center justify-between gap-3 rounded-lg border border-white/30 px-5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300">
            {applied ? "Review my applications" : "Start applying"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
          </Link>
        </div>
      </div>
    </motion.section>
  );
}
